"use client";
import React, { useMemo } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Select, SelectItem } from "@heroui/select";

import { ChipColor } from "@/components/ui/ColorChip";
import { StatusChip } from "@/components/ui/StatusChip";
import { getCategoryColor } from "@/lib/need-utils";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";
import { OrganizationTerminology } from "@/lib/services/terminologyService";

// Icons
const SearchIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const FilterIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const SortIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const MapIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    aria-hidden="true"
    className="w-4 h-4"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 18L18 6M6 6l12 12"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  </svg>
);

const CategoryIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5"
    fill="none"
    focusable="false"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

function applyFilterParam(
  params: URLSearchParams,
  key: string,
  value: string | string[] | null,
) {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    params.delete(key);
  } else if (Array.isArray(value)) {
    params.set(key, value.join(","));
  } else {
    params.set(key, value);
  }
}

function getToggledValues(
  key: string,
  id: string,
  selectedAreas: string[],
  selectedUrgency: string[],
): string[] {
  const currentValues =
    key === "areas" ? [...selectedAreas] : [...selectedUrgency];

  return currentValues.includes(id)
    ? currentValues.filter((item) => item !== id)
    : [...currentValues, id];
}

function savePreferredAreasToStorage(selectedAreas: string[]) {
  if (selectedAreas.length === 0) return;
  localStorage.setItem("preferredAreas", JSON.stringify(selectedAreas));
  alert(
    "Preferred areas saved! These areas will be auto-selected on future visits.",
  );
}

function renderClearSearchButton(
  localSearchQuery: string,
  onClear: () => void,
) {
  if (!localSearchQuery) return null;

  return (
    <Button
      isIconOnly
      aria-label="Clear search"
      className="rounded-full absolute right-2"
      data-testid="clear-search"
      size="sm"
      variant="light"
      onPress={onClear}
    >
      <CloseIcon />
    </Button>
  );
}

const SORT_OPTIONS = [
  { label: "Most Urgent", value: "urgency-desc" },
  { label: "Least Urgent", value: "urgency-asc" },
  { label: "Newest First", value: "date-desc" },
  { label: "Oldest First", value: "date-asc" },
  { label: "Most Kids", value: "kids-desc" },
  { label: "Fewest Kids", value: "kids-asc" },
  { label: "Most Items", value: "items-desc" },
  { label: "Fewest Items", value: "items-asc" },
  { label: "Due Date (Soonest)", value: "due-asc" },
  { label: "Due Date (Latest)", value: "due-desc" },
];

// Urgency options for the chip filter
const URGENCY_OPTIONS = [
  { color: "success" as ChipColor, id: "low", label: "Low Priority" },
  { color: "warning" as ChipColor, id: "medium", label: "Medium Priority" },
  { color: "pink" as ChipColor, id: "high", label: "High Priority" },
  { color: "danger" as ChipColor, id: "critical", label: "Critical" },
];

interface NeedsFilterProps {
  areas: {
    id: string;
    slug: string;
    name: string;
    state: string;
  }[];
  categories: {
    id: string;
    name: string;
  }[];
  initialFilters: {
    searchQuery: string;
    selectedAreas: string[]; // Now contains slugs instead of IDs
    selectedCategory: string;
    selectedUrgency: string[];
  };
  orgSlug: string;
  terminology: OrganizationTerminology;
}

export function NeedsFilter({
  areas,
  categories,
  initialFilters: {
    searchQuery,
    selectedAreas,
    selectedCategory,
    selectedUrgency,
  },
  orgSlug,
  terminology,
}: NeedsFilterProps) {
  const router = useRouter();
  const rawSearchParams = useSearchParams();
  const searchParams = rawSearchParams ?? new URLSearchParams();

  const [localSearchQuery, setLocalSearchQuery] = React.useState(searchQuery);

  const areasSet = useMemo(() => new Set(selectedAreas), [selectedAreas]);
  const urgencySet = useMemo(() => new Set(selectedUrgency), [selectedUrgency]);

  const currentSort = searchParams.get("sort") ?? "urgency-desc";

  // Get counts of active filters for badges
  const activeFiltersCount =
    (searchQuery ? 1 : 0) + areasSet.size + urgencySet.size;
  // Note: Category filters are now handled separately by CategoryFilters component

  // Update any filter and navigate
  const updateFilter = (key: string, value: string | string[] | null) => {
    const params = new URLSearchParams(searchParams.toString());

    applyFilterParam(params, key, value);
    void EventPublisher.publishEvent(EventKeys.ORG_NEED_FILTER_APPLY, {
      key,
      value,
    });
    router.push(`${routes.org.needs(orgSlug)}?${params.toString()}`);
  };

  // Toggle a chip selection
  const toggleChip = (key: string, id: string) => {
    updateFilter(
      key,
      getToggledValues(key, id, selectedAreas, selectedUrgency),
    );
  };

  // Clear all filters
  const clearFilters = () => {
    void EventPublisher.publishEvent(EventKeys.ORG_ACTION_CANCEL, {
      action: "clear_filters",
    });
    router.push(routes.org.needs(orgSlug));
  };

  return (
    <div
      aria-label="Filter options for needs"
      className="p-4 space-y-6 h-full"
      data-testid="needs-filter"
    >
      <h3 className="text-lg font-semibold mb-2" id="filter-title">
        Filters
      </h3>

      {/* Search */}
      <div className="space-y-2" role="search">
        <label className="flex items-center gap-2" id="search-label">
          <SearchIcon />
          <span className="text-sm font-medium">Search</span>
        </label>
        <Input
          aria-labelledby="search-label"
          className="w-full"
          data-testid="search-input"
          endContent={renderClearSearchButton(localSearchQuery, () => {
            setLocalSearchQuery("");
            updateFilter("q", "");
          })}
          placeholder={`Search ${terminology.need.plural.toLowerCase()}...`}
          size="sm"
          value={localSearchQuery}
          variant="bordered"
          onBlur={() => updateFilter("q", localSearchQuery)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              updateFilter("q", localSearchQuery);
            }
          }}
          onValueChange={(value) => {
            setLocalSearchQuery(value);
            updateFilter("q", value);
          }}
        />
      </div>

      {/* Sort Section */}
      <div aria-labelledby="sort-label" className="space-y-2" role="region">
        <label className="flex items-center gap-2" id="sort-label">
          <SortIcon />
          <span className="text-sm font-medium">Sort By</span>
        </label>
        <Select
          aria-labelledby="sort-label"
          className="w-full"
          selectedKeys={new Set([currentSort])}
          size="sm"
          variant="bordered"
          onChange={(e) => updateFilter("sort", e.target.value)}
        >
          {SORT_OPTIONS.map((option) => (
            <SelectItem key={option.value}>{option.label}</SelectItem>
          ))}
        </Select>
      </div>
      {/* Clear Filters */}
      {activeFiltersCount > 0 && (
        <Button
          aria-label={`Clear all filters (${activeFiltersCount} active)`}
          className="w-full"
          color="danger"
          data-testid="clear-filters"
          size="sm"
          variant="flat"
          onPress={clearFilters}
        >
          Clear All Filters ({activeFiltersCount})
        </Button>
      )}
      {/* Category Section */}
      <div aria-labelledby="category-label" className="space-y-2" role="region">
        <label className="flex items-center gap-2" id="category-label">
          <CategoryIcon />
          <span className="text-sm font-medium">
            {terminology.category.singular}
          </span>
        </label>
        <div
          aria-labelledby="category-label"
          className="flex flex-wrap gap-2"
          role="group"
        >
          {categories.map((category) => (
            <StatusChip
              key={category.id}
              aria-label={`${category.name} (${selectedCategory === category.id ? "selected" : "not selected"})`}
              aria-pressed={selectedCategory === category.id}
              className="cursor-pointer"
              color={getCategoryColor(category.id)}
              isCloseable={selectedCategory === category.id}
              size="sm"
              variant={selectedCategory === category.id ? "bordered" : "flat"}
              onClick={() => updateFilter("category", category.id)}
              onClose={
                selectedCategory === category.id
                  ? () => updateFilter("category", null)
                  : undefined
              }
            >
              {category.name}
            </StatusChip>
          ))}
        </div>
      </div>

      {/* Urgency Section */}
      <div aria-labelledby="urgency-label" className="space-y-2" role="region">
        <label className="flex items-center gap-2" id="urgency-label">
          <FilterIcon />
          <span className="text-sm font-medium">Urgency</span>
        </label>
        <div
          aria-labelledby="urgency-label"
          className="flex flex-wrap gap-2"
          role="group"
        >
          {URGENCY_OPTIONS.map((option) => (
            <StatusChip
              key={option.id}
              aria-label={`${option.label} (${urgencySet.has(option.id) ? "selected" : "not selected"})`}
              aria-pressed={urgencySet.has(option.id)}
              className="cursor-pointer"
              color={option.color}
              isCloseable={urgencySet.has(option.id)}
              size="sm"
              variant={urgencySet.has(option.id) ? "bordered" : "flat"}
              onClick={() => toggleChip("urgency", option.id)}
              onClose={
                urgencySet.has(option.id)
                  ? () => toggleChip("urgency", option.id)
                  : undefined
              }
            >
              {option.label}
            </StatusChip>
          ))}
        </div>
      </div>

      {/* Area Section */}
      <div aria-labelledby="area-label" className="space-y-2" role="region">
        <label className="flex items-center gap-2" id="area-label">
          <MapIcon />
          <span className="text-sm font-medium">
            {terminology.area.singular}
          </span>
        </label>
        <div
          aria-labelledby="area-label"
          className="flex flex-wrap gap-2"
          role="group"
        >
          {[...areas]
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
            )
            .map((area) => (
              <StatusChip
                key={area.id}
                aria-label={`${area.name}, ${area.state} (${areasSet.has(area.slug) ? "selected" : "not selected"})`}
                aria-pressed={areasSet.has(area.slug)}
                className="cursor-pointer"
                color="primary"
                isCloseable={areasSet.has(area.slug)}
                size="sm"
                variant={areasSet.has(area.slug) ? "bordered" : "flat"}
                onClick={() => toggleChip("areas", area.slug)}
                onClose={
                  areasSet.has(area.slug)
                    ? () => toggleChip("areas", area.slug)
                    : undefined
                }
              >
                {area.name}, {area.state}
              </StatusChip>
            ))}
        </div>
        {/* Save Preferred Areas Button */}
        {areasSet.size > 0 && (
          <Button
            className="mt-2"
            color="primary"
            size="sm"
            variant="flat"
            onPress={() => savePreferredAreasToStorage(selectedAreas)}
          >
            Set as Preferred {terminology.area.plural}
          </Button>
        )}
      </div>
    </div>
  );
}
